// Service Firestore pour la gestion des messages système (côté admin)
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  collectionGroup,
  where,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const COLLECTION = 'systemMessages';

/**
 * Récupère tous les messages système pour l'admin (sans filtres)
 * @returns {Promise<Array>}
 */
export const getAllMessages = async () => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Erreur récupération messages:', err);
    throw err;
  }
};

/**
 * Récupère un message par son ID
 * @param {string} messageId
 * @returns {Promise<Object|null>}
 */
export const getMessageById = async (messageId) => {
  try {
    const d = await getDoc(doc(db, COLLECTION, messageId));
    if (!d.exists()) return null;
    return { id: d.id, ...d.data() };
  } catch (err) {
    console.error('Erreur récupération message:', err);
    throw err;
  }
};

/**
 * Crée un nouveau message système
 * @param {Object} data - Données du message
 * @param {boolean} publish - Publier immédiatement ou sauvegarder en brouillon
 * @returns {Promise<string>} ID du document créé
 */
export const createMessage = async (data, publish = true) => {
  try {
    const defaultExpiry = new Date();
    defaultExpiry.setMonth(defaultExpiry.getMonth() + 6);

    const messageData = {
      title: data.title,
      content: data.content,
      type: data.type || 'news',
      priority: data.priority || 'normal',
      targetAudience: data.targetAudience || 'all',
      createdAt: serverTimestamp(),
      publishedAt: publish
        ? (data.publishedAt ? Timestamp.fromDate(new Date(data.publishedAt)) : serverTimestamp())
        : null,
      expiresAt: data.expiresAt
        ? Timestamp.fromDate(new Date(data.expiresAt))
        : Timestamp.fromDate(defaultExpiry),
      imageUrl: data.imageUrl || null,
      actionButton: data.actionButton?.label ? data.actionButton : null,
      sendPushNotification: false,
      pushSent: false,
    };

    const ref = await addDoc(collection(db, COLLECTION), messageData);
    return ref.id;
  } catch (err) {
    console.error('Erreur création message:', err);
    throw err;
  }
};

/**
 * Met à jour un message existant
 * @param {string} messageId
 * @param {Object} data
 * @param {boolean|null} publish - true=publier, false=dépublier, null=ne pas changer
 * @returns {Promise<void>}
 */
export const updateMessage = async (messageId, data, publish = null) => {
  try {
    const defaultExpiry = new Date();
    defaultExpiry.setMonth(defaultExpiry.getMonth() + 6);

    const updateData = {
      title: data.title,
      content: data.content,
      type: data.type || 'news',
      priority: data.priority || 'normal',
      targetAudience: data.targetAudience || 'all',
      updatedAt: serverTimestamp(),
      expiresAt: data.expiresAt
        ? Timestamp.fromDate(new Date(data.expiresAt))
        : Timestamp.fromDate(defaultExpiry),
      imageUrl: data.imageUrl || null,
      actionButton: data.actionButton?.label ? data.actionButton : null,
    };

    // Gérer publishedAt selon l'action
    if (publish === true) {
      updateData.publishedAt = data.publishedAt
        ? Timestamp.fromDate(new Date(data.publishedAt))
        : serverTimestamp();
    } else if (publish === false) {
      updateData.publishedAt = null;
    }

    await updateDoc(doc(db, COLLECTION, messageId), updateData);
  } catch (err) {
    console.error('Erreur mise à jour message:', err);
    throw err;
  }
};

/**
 * Supprime un message système
 * @param {string} messageId
 * @returns {Promise<void>}
 */
export const deleteMessage = async (messageId) => {
  try {
    await deleteDoc(doc(db, COLLECTION, messageId));
  } catch (err) {
    console.error('Erreur suppression message:', err);
    throw err;
  }
};

/**
 * Upload une image dans Firebase Storage
 * @param {File} file
 * @param {string} messageId - ID du message (ou 'temp' pour nouveau)
 * @returns {Promise<string>} URL publique de l'image
 */
export const uploadMessageImage = async (file, messageId = 'temp') => {
  try {
    const ext = file.name.split('.').pop();
    const storageRef = ref(storage, `system-messages/${messageId}_${Date.now()}.${ext}`);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    return url;
  } catch (err) {
    console.error('Erreur upload image message:', err);
    throw err;
  }
};

/**
 * Supprime une image de Firebase Storage
 * @param {string} imageUrl - URL Firebase Storage
 * @returns {Promise<void>}
 */
export const deleteMessageImage = async (imageUrl) => {
  if (!imageUrl || !imageUrl.includes('firebasestorage')) return;
  try {
    const imgRef = ref(storage, imageUrl);
    await deleteObject(imgRef);
  } catch (err) {
    console.warn('Erreur suppression image (ignorée):', err);
  }
};

/**
 * Calcule les statistiques d'un message (lectures)
 * @param {string} messageId
 * @returns {Promise<{ readCount: number, lastReadAt: Date|null }>}
 */
export const getMessageStats = async (messageId) => {
  try {
    const q = query(
      collectionGroup(db, 'messageStatus'),
      where('messageId', '==', messageId),
      where('read', '==', true)
    );
    const snap = await getDocs(q);

    let lastReadAt = null;
    snap.docs.forEach((d) => {
      const readAt = d.data().readAt?.toDate?.();
      if (readAt && (!lastReadAt || readAt > lastReadAt)) {
        lastReadAt = readAt;
      }
    });

    return {
      readCount: snap.size,
      lastReadAt,
    };
  } catch (err) {
    console.error('Erreur récupération stats message:', err);
    return { readCount: 0, lastReadAt: null };
  }
};
